/**
 * Salle POP! — lobby, manche en cours, classement final.
 *
 * - Le code de salle est affiché GROS : on le dicte à voix haute à travers
 *   une pièce bruyante.
 * - Le compte à rebours est calculé depuis `deadlineAt`, l'horloge du SERVEUR
 *   (date absolue ISO) : aucun décompte local qui dériverait.
 * - La zone de réponse n'est active que si c'est mon tour.
 */
import { useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import type { Player } from '@pop/contracts';
import { useRoom } from '../useRoom.js';

interface RoomProps {
  readonly code: string;
  readonly playerId: string;
  readonly onLeave: () => void;
}

/** Horloge locale qui avance pour faire décroître le compte à rebours. */
function useNowTick(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) {
      return;
    }
    const id = window.setInterval(() => {
      setNow(Date.now());
    }, 250);
    return () => {
      window.clearInterval(id);
    };
  }, [active]);
  return now;
}

function formatCountdown(deadlineAt: string, now: number): string {
  const remaining = Date.parse(deadlineAt) - now;
  if (Number.isNaN(remaining) || remaining <= 0) {
    return '0 s';
  }
  return `${Math.ceil(remaining / 1000)} s`;
}

function PlayerRow({
  player,
  isHost,
  isActive,
  isMe,
}: {
  readonly player: Player;
  readonly isHost: boolean;
  readonly isActive: boolean;
  readonly isMe: boolean;
}): JSX.Element {
  return (
    <li
      className={`player${isMe ? ' player-me' : ''}${isActive ? ' player-active' : ''}`}
      // Ancré sur le pseudo plutôt que sur un rang : les tests restent lisibles
      // et survivent à un changement d'ordre dans la liste.
      data-testid={`player-${player.pseudo}`}
    >
      <span className="player-name">
        {player.pseudo}
        {isMe && <span className="badge">toi</span>}
        {isHost && <span className="badge badge-host">hôte</span>}
        {isActive && <span className="badge badge-turn">joue</span>}
        {!player.connected && <span className="badge badge-off">déconnecté</span>}
      </span>
      <span
        className="player-score"
        aria-label={`${player.score} points`}
        data-testid={`score-${player.pseudo}`}
      >
        {player.score} pt{player.score > 1 ? 's' : ''}
      </span>
    </li>
  );
}

export function Room({ code, playerId, onLeave }: RoomProps) {
  const { state, connected, lastResolution, finished, rejection, start, answer, dismissRejection } =
    useRoom(code, playerId);
  const [answerText, setAnswerText] = useState('');

  const round = state?.round ?? null;
  const now = useNowTick(state?.status === 'playing' && round !== null);

  // Quand la manche change, on repart d'un champ vide.
  useEffect(() => {
    setAnswerText('');
  }, [round?.index]);

  function handleAnswer(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const text = answerText.trim();
    if (text.length === 0) {
      return;
    }
    answer(text);
    setAnswerText('');
  }

  if (state === null) {
    return (
      <div className="room">
        <p className="muted" role="status">
          {connected ? 'Chargement de la salle…' : 'Reconnexion à la salle…'}
        </p>
        <button className="btn btn-ghost" type="button" onClick={onLeave}>
          Retour à l&apos;accueil
        </button>
      </div>
    );
  }

  const isHost = state.hostId === playerId;
  const activePlayer = state.players.find((p) => p.id === round?.activePlayerId) ?? null;
  const isMyTurn =
    state.status === 'playing' && round !== null && round.activePlayerId === playerId;
  const sortedPlayers = [...state.players].sort((a, b) => b.score - a.score);

  return (
    <div className="room">
      <header className="room-header">
        <div>
          <p className="room-code-label">Code de la salle</p>
          <p
            className="room-code"
            aria-label={`Code de salle ${state.code}`}
            data-testid="room-code"
          >
            {state.code}
          </p>
        </div>
        <span className={`dot${connected ? ' dot-on' : ' dot-off'}`} role="status">
          {connected ? 'connecté' : 'reconnexion…'}
        </span>
      </header>

      {rejection !== null && (
        <p className="alert alert-error" role="alert">
          {rejection.message}{' '}
          <button className="link" type="button" onClick={dismissRejection}>
            Ignorer
          </button>
        </p>
      )}

      <section className="card" aria-labelledby="players-title">
        <h2 id="players-title" className="card-title">
          Joueurs ({state.players.length})
        </h2>
        <ul className="players">
          {state.players.map((player) => (
            <PlayerRow
              key={player.id}
              player={player}
              isHost={player.id === state.hostId}
              isActive={player.id === round?.activePlayerId}
              isMe={player.id === playerId}
            />
          ))}
        </ul>
      </section>

      {state.status === 'lobby' && (
        <section className="card" aria-labelledby="lobby-title">
          <h2 id="lobby-title" className="card-title">
            En attente
          </h2>
          {isHost ? (
            <>
              <p className="muted">Tu es l&apos;hôte : lance quand tout le monde est là.</p>
              <button
                className="btn btn-primary"
                type="button"
                onClick={start}
                disabled={state.players.length < 2}
              >
                Lancer la partie
              </button>
              {state.players.length < 2 && (
                <p className="muted">Il faut au moins deux joueurs pour lancer.</p>
              )}
            </>
          ) : (
            <p className="muted">En attente de l&apos;hôte… Dicte le code à tes amis !</p>
          )}
        </section>
      )}

      {state.status === 'playing' && round !== null && (
        <section className="card" aria-labelledby="round-title">
          <h2 id="round-title" className="card-title">
            Manche {round.index + 1} / {round.total}
          </h2>
          <p className="prompt" data-testid="round-prompt">
            {round.prompt}
          </p>
          <p className="turn">
            {isMyTurn ? (
              <strong>À toi de répondre !</strong>
            ) : (
              <>
                C&apos;est à <strong>{activePlayer?.pseudo ?? '…'}</strong> de répondre.
              </>
            )}
          </p>
          <p
            className="countdown"
            role="timer"
            aria-label={`Temps restant : ${formatCountdown(round.deadlineAt, now)}`}
          >
            ⏱ {formatCountdown(round.deadlineAt, now)}
          </p>

          {isMyTurn ? (
            <form onSubmit={handleAnswer} className="answer-form">
              <label className="field" htmlFor="answer">
                <span className="field-label">Ta réponse</span>
                <input
                  id="answer"
                  className="input"
                  type="text"
                  maxLength={120}
                  autoComplete="off"
                  placeholder="Écris ta réponse…"
                  value={answerText}
                  onChange={(event) => {
                    setAnswerText(event.target.value);
                  }}
                />
              </label>
              <button
                className="btn btn-primary"
                type="submit"
                disabled={answerText.trim().length === 0}
              >
                Envoyer
              </button>
            </form>
          ) : (
            <p className="muted">La zone de réponse s&apos;activera quand ce sera ton tour.</p>
          )}

          {lastResolution !== null && lastResolution.index < round.index && (
            <p className="muted">
              {lastResolution.reason === 'timeout'
                ? 'Manche précédente : temps écoulé, aucun point.'
                : 'Manche précédente : réponse acceptée, +1 point.'}
            </p>
          )}
        </section>
      )}

      {state.status === 'finished' && (
        <section className="card" aria-labelledby="ranking-title">
          <h2 id="ranking-title" className="card-title">
            Partie terminée 🎉
          </h2>
          <ol className="ranking">
            {sortedPlayers.map((player, position) => {
              const isWinner =
                finished !== null ? finished.winnerIds.includes(player.id) : position === 0;
              return (
                <li key={player.id} className={`rank${isWinner ? ' rank-winner' : ''}`}>
                  <span className="rank-position">{position + 1}.</span>{' '}
                  {isWinner && <span aria-hidden="true">🏆 </span>}
                  <strong>{player.pseudo}</strong> — {player.score} pt
                  {player.score > 1 ? 's' : ''}
                  {player.id === playerId && <span className="badge">toi</span>}
                </li>
              );
            })}
          </ol>
          <button className="btn btn-secondary" type="button" onClick={onLeave}>
            Retour à l&apos;accueil
          </button>
        </section>
      )}

      {state.status !== 'finished' && (
        <button className="btn btn-ghost" type="button" onClick={onLeave}>
          Quitter la salle
        </button>
      )}
    </div>
  );
}
